import React, { useMemo, useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'

import { Alert, Button, Input, Modal, Skeleton, TextArea } from '@repo/ui'

import { SelectField } from 'components/input/fields/SelectField'
import { useSnackbar } from 'contexts'
import { useCompaniesQuery } from 'hooks/api/useCompaniesQuery'
import { listedCompanies } from 'utils/listedCompany'
import { useCompanyUsersQuery } from 'hooks/api/useCompanyUsersQuery'
import {
  useReattributeReferral,
  useReferralStaffActions,
  useReverseReferralCredits,
  type StaffReferral,
} from 'hooks/api/useInternalReferrals'
import { formatCount } from 'constants/referrals'
import { absoluteDateTime, getErrorMessage } from 'utils'

import { newIdempotencyKey, prospectOf, referredByOf } from './referralLabels'

/**
 * VRG-658 — the two staff row actions on a referral, platform admin only (Spencer's default,
 * 2026-10-05), plus the history that records them. The API is the real boundary: both
 * endpoints are `@RequireAdmin({roles: [PlatformAdmin]})`, take the referral row lock and are
 * idempotent on the key each dialog mints once when it opens.
 */

const MIN_REASON = 5

/**
 * Take referral credits back from the REFERRING company by hand — e.g. a bare charge refund,
 * which the reward engine leaves to staff because only credit notes adjust automatically.
 *
 * The consequence is named before anything is written (§43): the company's balance never goes
 * below zero, so part of a reversal can be forgiven, and the dialog says so up front.
 */
export const ReverseCreditsModal = ({
  referral,
  onClose,
}: {
  referral: StaffReferral | null
  onClose: () => void
}) => {
  const open = !!referral
  return open ? <ReverseCreditsDialog key={referral!.id} referral={referral!} onClose={onClose} /> : null
}

const ReverseCreditsDialog = ({ referral, onClose }: { referral: StaffReferral; onClose: () => void }) => {
  const { open: notify } = useSnackbar()
  const reverse = useReverseReferralCredits()
  const [key] = useState(newIdempotencyKey)
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const max = referral.credits.net
  const n = Number(amount)
  const amountValid = Number.isInteger(n) && n >= 1 && n <= max
  const canSubmit = amountValid && reason.trim().length >= MIN_REASON && !reverse.isPending
  const brand = referredByOf(referral)?.primary ?? 'the referring brand'

  const submit = async () => {
    if (!canSubmit) return
    setError(null)
    try {
      const out = await reverse.mutateAsync({ referralId: referral.id, amount: n, reason: reason.trim(), idempotencyKey: key })
      notify({
        type: 'success',
        title:
          out.forgiven > 0
            ? `Reversed ${formatCount(out.applied)} credits. ${formatCount(out.forgiven)} forgiven (the balance would have gone below zero).`
            : `Reversed ${formatCount(out.applied)} credits.`,
      })
      onClose()
    } catch (e) {
      setError(getErrorMessage(e))
    }
  }

  return (
    <Modal
      title="Reverse referral credits"
      open
      onClose={onClose}
      description={`${prospectOf(referral).primary}, referred by ${brand}. This referral has earned ${formatCount(max)} credits.`}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button destructive onClick={submit} disabled={!canSubmit} loading={reverse.isPending}>
            Reverse credits
          </Button>
        </>
      }
    >
      <Stack spacing={5}>
        <Alert
          severity="warning"
          title="This takes activations back from the referring brand."
          description="It writes a reversal to their activation balance, with your name and reason. Their balance never goes below zero: anything it cannot cover is forgiven and reported."
        />
        <Input
          label="Credits to reverse"
          type="number"
          value={amount}
          onChange={setAmount}
          inputProps={{ min: 1, max, step: 1 }}
          error={amount !== '' && !amountValid}
          helperText={amount !== '' && !amountValid ? `A whole number from 1 to ${formatCount(max)}.` : undefined}
        />
        <TextArea
          label="Reason"
          value={reason}
          onChange={setReason}
          minRows={2}
          placeholder="For example: refunded outside Stripe credit notes"
        />
        {error && <Alert severity="error" title={error} />}
      </Stack>
    </Modal>
  )
}

/**
 * Point a referral at a different referring brand (and optionally the member who sent it).
 * The API refuses one that has earned credits, so the dialog says that before the attempt
 * rather than letting staff discover it from an error.
 */
export const ReattributeReferralModal = ({
  referral,
  onClose,
}: {
  referral: StaffReferral | null
  onClose: () => void
}) => (referral ? <ReattributeDialog key={referral.id} referral={referral} onClose={onClose} /> : null)

const ReattributeDialog = ({ referral, onClose }: { referral: StaffReferral; onClose: () => void }) => {
  const { open: notify } = useSnackbar()
  const reattribute = useReattributeReferral()
  const [key] = useState(newIdempotencyKey)
  const [companyId, setCompanyId] = useState('')
  const [userId, setUserId] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: companies, isLoading: companiesLoading } = useCompaniesQuery({ limit: 200 })
  const { data: members, isLoading: membersLoading } = useCompanyUsersQuery({ id: companyId, limit: 200 }, { enabled: !!companyId })

  const companyOptions = useMemo(
    () =>
      listedCompanies(companies)
        .filter((c) => c.id !== referral.referred_company?.id)
        .map((c) => ({ value: c.id, label: c.name })),
    [companies, referral.referred_company?.id],
  )
  const memberOptions = useMemo(
    () => [
      { value: '', label: 'No one in particular' },
      ...(members?.data ?? []).map((m) => ({
        value: m.id,
        label: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email,
        rightLabel: m.email,
      })),
    ],
    [members],
  )

  const hasCredits = referral.credits.net > 0
  const canSubmit = !hasCredits && !!companyId && reason.trim().length >= MIN_REASON && !reattribute.isPending
  const from = referredByOf(referral)

  const submit = async () => {
    if (!canSubmit) return
    setError(null)
    try {
      await reattribute.mutateAsync({
        referralId: referral.id,
        referrerCompanyId: companyId,
        referrerUserId: userId || null,
        reason: reason.trim(),
        idempotencyKey: key,
      })
      notify({ type: 'success', title: 'Referral re-attributed.' })
      onClose()
    } catch (e) {
      setError(getErrorMessage(e))
    }
  }

  return (
    <Modal
      title="Re-attribute referral"
      open
      onClose={onClose}
      description={`${prospectOf(referral).primary}. Currently referred by ${from ? `${from.primary}${from.secondary ? ` (${from.secondary})` : ''}` : 'nobody'}.`}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={reattribute.isPending}>
            Re-attribute
          </Button>
        </>
      }
    >
      <Stack spacing={5}>
        {hasCredits ? (
          <Alert
            severity="warning"
            title={`This referral has earned ${formatCount(referral.credits.net)} credits.`}
            description="Reverse them first. Moving a referral that has earned credits would leave the credits with the old brand while the referral moved."
          />
        ) : (
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Future credits from this referral will go to the brand you choose. The change and
            your reason are recorded in its history.
          </Typography>
        )}
        <SelectField
          label="Referring brand"
          options={companyOptions}
          value={companyId}
          onChange={(v) => {
            setCompanyId(String(v))
            setUserId('')
          }}
          search
          placeholder={companiesLoading ? 'Loading companies' : 'Choose a company'}
          disabled={hasCredits}
        />
        <SelectField
          label="Sent by (optional)"
          options={memberOptions}
          value={userId}
          onChange={(v) => setUserId(String(v))}
          placeholder={membersLoading ? 'Loading members' : 'No one in particular'}
          disabled={hasCredits || !companyId}
        />
        <TextArea label="Reason" value={reason} onChange={setReason} minRows={2} disabled={hasCredits} />
        {error && <Alert severity="error" title={error} />}
      </Stack>
    </Modal>
  )
}

/** Every staff action on a referral, newest first — the audit trail behind the two actions. */
export const ReferralHistoryModal = ({
  referral,
  onClose,
}: {
  referral: StaffReferral | null
  onClose: () => void
}) => {
  const { data, isLoading } = useReferralStaffActions(referral?.id ?? null)
  const rows = data?.data ?? []
  return (
    <Modal
      title="Referral history"
      open={!!referral}
      onClose={onClose}
      description={referral ? prospectOf(referral).primary : undefined}
    >
      {isLoading ? (
        <Stack spacing={3}>
          <Skeleton variant="rounded" height={64} />
          <Skeleton variant="rounded" height={64} />
        </Stack>
      ) : rows.length === 0 ? (
        <Typography variant="body3" color="text.secondary">
          No staff actions on this referral.
        </Typography>
      ) : (
        <Stack spacing={4}>
          {rows.map((a) => (
            <Box key={a.id}>
              <Typography variant="body2">
                {a.action === 'reversal' ? `Reversed ${formatCount(a.credits)} credits` : 'Re-attributed'}
              </Typography>
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {a.reason}
              </Typography>
              <Typography variant="body4" color="text.secondary">
                {a.actor.name ?? a.actor.email ?? 'Former staff member'} · {absoluteDateTime(a.created_at)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Modal>
  )
}
